import { ModuleNavTitle, State } from '@course/components/module-helpers';
import { useEffect, useMemo } from 'preact/hooks';
import { ProgressBar } from '@components/progress-bar';
import { URLS } from 'shared/urls';
import { useLocalStorageState, useScrollRef } from 'client/lib/hooks';
import { StudentPageNavHeader } from '@course/components/page-nav-bar';
import { LessonBadges } from '@course/components/lesson-badges';
import { useIntl } from 'shared/intl/use-intl';
import { emptyLessonTitle } from 'shared/terminology';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { Button } from '@components/buttons';
import { IcoChevronDown, IcoChevronRight, IcoExternalLink } from '@components/icons';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';

export function HiddenModulesNav({
  modules,
  course,
  currentLessonId,
}: {
  modules: State['modules'];
  course: State['course'];
  currentLessonId?: UUID;
}) {
  return (
    <span class="p-6 py-2 hidden md:block dark:opacity-50">
      {course.modules.map((moduleId) => {
        const module = modules[moduleId];

        return (
          <>
            <span class="block w-4 mb-2 h-0.5 bg-gray-400 rounded-sm" />
            {module.lessons.map((lessonId) => (
              <span
                key={lessonId}
                class={`block w-4 ml-1 mb-1.5 h-0.5 rounded ${
                  lessonId === currentLessonId ? 'bg-indigo-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </>
        );
      })}
    </span>
  );
}

export function ModulesNav({
  completedLessons,
  currentLessonId,
  modules,
  lessons,
  lessonIds,
  course,
  canManageCourse,
  membershipDate,
  unlockedModuleIds,
}: {
  currentLessonId?: UUID;
  course: State['course'];
  completedLessons: State['completedLessons'];
  modules: State['modules'];
  lessons: State['lessons'];
  lessonIds: UUID[];
  canManageCourse: boolean;
  membershipDate?: Date;
  unlockedModuleIds?: UUID[];
}) {
  const tenant = useCurrentTenant();
  const intl = useIntl();
  const currentUser = useCurrentUser()!;
  const completedLessonsSet = useMemo(() => new Set(completedLessons), [completedLessons]);
  const scrollRef = useScrollRef();
  const [collapsedModules, setCollapsedModules] = useLocalStorageState<string[]>(
    `collapsed.student-modules.${course.id}`,
    [],
  );

  useEffect(() => {
    if (!currentLessonId) {
      return;
    }
    const moduleId = course.modules.find((mid) => modules[mid].lessons.includes(currentLessonId));
    if (moduleId && collapsedModules.includes(moduleId)) {
      setCollapsedModules(collapsedModules.filter((id) => id !== moduleId));
    }
  }, [currentLessonId]);

  return (
    <div class="pb-96">
      <div class="mb-6 text-gray-700 dark:text-gray-200">
        <StudentPageNavHeader
          title={course.id === AIP_COURSE_ID ? intl('Resources') : intl('Modules & Lessons')}
        />
        <div class="mb-2 flex justify-between items-center font-display font-medium text-gray-500 dark:text-gray-200">
          {intl('Course Progress')}
          <span class="text-sm">
            {intl('{completed:number} of {total:number}', {
              completed: completedLessons.length,
              total: lessonIds.length,
            })}
          </span>
        </div>
        <ProgressBar
          progress={(completedLessons.length * 100) / lessonIds.length}
          showPercent={false}
          bgClass="bg-gray-100 dark:bg-gray-600"
          barClass="bg-green-400 dark:bg-green-600"
        />
        {course.modules.length > 1 && (
          <div class="flex justify-end mt-2">
            <Button
              class="inline-flex gap-1 items-center hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-xs p-1"
              onClick={() =>
                collapsedModules.length
                  ? setCollapsedModules([])
                  : setCollapsedModules(course.modules)
              }
            >
              <span class="text-gray-500 dark:text-gray-400">
                {collapsedModules.length ? intl('Expand all') : intl('Collapse all')}
              </span>
              {collapsedModules.length ? <IcoChevronRight /> : <IcoChevronDown />}
            </Button>
          </div>
        )}
      </div>
      {course.modules.map((moduleId) => {
        const module = modules[moduleId];
        const firstLesson = lessons[module.lessons[0]];
        const isExpanded = !collapsedModules.includes(moduleId);

        return (
          <div key={moduleId} class="flex flex-col py-4 relative mb-2">
            <div class="flex items-center gap-2 mb-4">
              <a
                class="grow text-gray-500 dark:text-gray-400"
                href={
                  firstLesson
                    ? URLS.student.lesson({
                        course,
                        lesson: firstLesson,
                      })
                    : undefined
                }
              >
                <ModuleNavTitle
                  isAbsoluteSchedule={course.isAbsoluteSchedule}
                  module={module}
                  accessFormat={course.accessFormat}
                  canManageCourse={canManageCourse}
                  membershipDate={membershipDate}
                  isUnlocked={unlockedModuleIds?.includes(moduleId)}
                />
              </a>
              <Button
                class="inline-flex shrink-0 gap-1 items-center hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-1"
                onClick={() => {
                  setCollapsedModules(
                    isExpanded
                      ? [...collapsedModules, moduleId]
                      : collapsedModules.filter((id) => id !== moduleId),
                  );
                }}
              >
                {!isExpanded && <span class="text-gray-400 text-xs">{module.lessons.length}</span>}
                {isExpanded ? (
                  <IcoChevronDown class="text-gray-400" />
                ) : (
                  <IcoChevronRight class="text-gray-400" />
                )}
              </Button>
            </div>
            {isExpanded && (
              <div class="ml-0.5 border-l-2 border-gray-200 dark:border-gray-800 dark:text-gray-400">
                {module.lessons.map((a) => {
                  const isSelected = a === currentLessonId;
                  const lesson = lessons[a];
                  const isComplete = completedLessonsSet.has(a);

                  return (
                    <span
                      key={a}
                      ref={isSelected ? scrollRef : undefined}
                      class={`mr-px tex-sm flex flex-col relative w-full hover:text-indigo-600 dark:hover:text-white hover:theme-link ${
                        isSelected
                          ? 'js-scroll-to relative z-10 text-indigo-600 theme-link dark:text-sky-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <a
                        id={`lesson-${a}`}
                        class={`p-4 py-2 mb-1 text-inherit font-normal flex items-center`}
                        href={URLS.student.lesson({
                          course,
                          lesson,
                        })}
                      >
                        <span class="inline-flex">
                          <span
                            class={`rounded-full h-1.5 w-1.5 -ml-5 relative z-10 ${
                              isSelected
                                ? 'bg-indigo-400 dark:bg-sky-400 bg-theme-primary'
                                : isComplete
                                  ? 'bg-green-300 dark:bg-green-900'
                                  : ''
                            }`}
                          ></span>
                        </span>
                        <span
                          class={`grow whitespace-nowrap text-ellipsis overflow-hidden ${
                            isSelected ? 'font-semibold' : ''
                          }`}
                        >
                          {lesson.title || emptyLessonTitle(tenant)}
                        </span>
                        <LessonBadges
                          lesson={lesson}
                          hasDiscussion={!!lesson.discussion?.isEnabled}
                          isSelected={isSelected}
                          isComplete={isComplete}
                        />
                      </a>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {course.hasEnabledCert && (
        <footer class="border-t border-gray-500/50 pt-6 mt-4 border-dashed">
          <Button
            href={URLS.student.certificate({ course, userId: currentUser.id })}
            target="_blank"
            class="flex gap-2 border border-gray-200 dark:border-gray-700 rounded-md p-2 items-center justify-center hover:bg-gray-100 text-inherit dark:hover:bg-gray-700 active:bg-gray-500"
          >
            <span>{intl('Certificate of Completion')}</span>
            <IcoExternalLink />
          </Button>
        </footer>
      )}
    </div>
  );
}
